import { Vue, Component } from 'vue-property-decorator';
import { beforeDestroy, mounted } from '@/Utility/Decorators';

/**
 * Configuration for sticky scroll behavior
 */
interface IStickyScrollConfig {
	effects: {
		blur?: number;
		hideSelector?: string;
		opacity?: number;
	};

	/**
	 * CSS selector for the element to apply sticky effects to
	 */
	elementSelector: string;

	/**
	 * Which element to listen for scroll events on:
	 * - 'self' (default): this.$el
	 * - 'parent': this.$el.parentElement
	 * - 'page': traverse up to find .page element
	 * - CSS selector string: query for that element
	 */
	scrollContainer: 'self' | 'parent' | 'page' | string;

	/**
	 * Scroll threshold in pixels to trigger sticky effects
	 */
	scrollThreshold: number;
}

/**
 * Mixin that provides sticky scroll effects for elements
 *
 * @package Mixins
 * @project ChalkySticks App
 */
@Component
export class StickyScroll extends Vue {
	/**
	 * Tracks scroll state
	 *
	 * @type boolean
	 */
	protected hasScrolled: boolean = false;

	/**
	 * Reference to the resolved scroll container element
	 *
	 * @type Element | null
	 */
	protected scrollContainerElement: Element | null = null;

	/**
	 * Configuration for sticky behavior
	 *
	 * @type IStickyScrollConfig
	 */
	protected stickyConfig: IStickyScrollConfig = {
		effects: {
			blur: 5,
			hideSelector: 'figcaption',
			opacity: 1,
		},
		elementSelector: '.ui-pagehero',
		scrollContainer: 'self',
		scrollThreshold: 150,
	};

	/**
	 * Resolves the scroll container element based on stickyConfig.scrollContainer
	 *
	 * @return Element | null
	 */
	protected resolveScrollContainer(): Element | null {
		const containerType = this.stickyConfig.scrollContainer || 'self';

		switch (containerType) {
			case 'self':
				return this.$el;

			case 'parent':
				return this.$el?.parentElement || null;

			case 'page':
				// Traverse up to find the .page element
				let element = this.$el?.parentElement;

				while (element) {
					if (element.classList.contains('page')) {
						return element;
					}

					element = element.parentElement;
				}

				// Fallback to self if no .page found
				return this.$el;

			default:
				// Treat as CSS selector
				if (containerType.startsWith('.') || containerType.startsWith('#')) {
					return document.querySelector(containerType);
				}

				return this.$el;
		}
	}

	/**
	 * Attaches scroll observer for sticky effects
	 *
	 * @return void
	 */
	@mounted
	protected attachStickyScrollObserver(): void {
		this.scrollContainerElement = this.resolveScrollContainer();

		if (this.scrollContainerElement) {
			this.scrollContainerElement.addEventListener('scroll', this.Handle_OnStickyScroll, { passive: true });
		}
	}

	/**
	 * Removes scroll observer
	 *
	 * @return void
	 */
	@beforeDestroy
	protected detachStickyScrollObserver(): void {
		if (this.scrollContainerElement) {
			this.scrollContainerElement.removeEventListener('scroll', this.Handle_OnStickyScroll);
		}
	}

	/**
	 * Updates the visual state of sticky elements
	 *
	 * @return void
	 */
	protected updateStickyElementState(): void {
		// This can be overridden for custom behavior
		// Default implementation uses CSS classes
	}

	// region: Event Handlers
	// ---------------------------------------------------------------------------

	/**
	 * @param Event e
	 * @return Promise<void>
	 */
	protected async Handle_OnStickyScroll(e: Event): Promise<void> {
		if (!this.scrollContainerElement) {
			return;
		}

		const scrollTop = this.scrollContainerElement.scrollTop;
		const wasScrolled = this.hasScrolled;

		this.hasScrolled = scrollTop > this.stickyConfig.scrollThreshold;

		// Only update DOM if state changed
		if (wasScrolled !== this.hasScrolled) {
			this.updateStickyElementState();
		}
	}

	// endregion: Event Handlers
}
